import React, { useState } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import styles from '../AboutLocation.module.scss';
import featureStyles from './FeatureAtLocationCard.module.scss';
import { FEATURE_TYPES, MAP_LAYER_IDS, SIDEBAR_VIEWS } from '../../../../lib/constants';
import { prettyNumber, metersToMiles, squareMetersToAcres } from '../../../../lib/utils';
import ChevronDown from '../../../../assets/svg/Feather/ChevronDown';
import Info from '../../../../assets/svg/Feather/Info';
import Center from '../../../../assets/svg/Remix/Center';
import AreaIcon from '../../../../assets/svg/AreaIcon';
import LineIcon from '../../../../assets/svg/LineIcon';
import WaypointIcon from '../../../../assets/svg/WaypointIcon';

function getFeatureIcon(ghType) {
  switch (ghType) {
    case FEATURE_TYPES.Polygon: return <AreaIcon width={18} color='#FAFAFA' />;
    case FEATURE_TYPES.Point: return <WaypointIcon icon='gohunt-logo' width={18} color='#FFFFFF' />;
    case FEATURE_TYPES.Polyline: return <LineIcon width={18} color='#FAFAFA' />;
    case FEATURE_TYPES.Tracks: return <LineIcon width={18} color='#FAFAFA' />;
    default: return <LineIcon width={18} color='#FAFAFA' />;
  }
}

function getFeatureStat(feature, hideCoords) {
  const { ghType } = feature.properties;
  switch (ghType) {
    case FEATURE_TYPES.Point:
      if (hideCoords) return null;
      return `${feature.geometry.coordinates[1].toFixed(4)}, ${feature.geometry.coordinates[0].toFixed(4)}`;
    case FEATURE_TYPES.Polyline:
    case FEATURE_TYPES.Tracks: {
      const distance = feature.properties.distance;
      if (distance == null || Number.isNaN(Number(distance))) return null;
      return `${prettyNumber(metersToMiles(distance))} miles`;
    }
    case FEATURE_TYPES.Polygon: {
      const area = feature.properties.area;
      if (area == null || Number.isNaN(Number(area))) return null;
      const acres = squareMetersToAcres(area);
      if (Number.isNaN(acres)) return null;
      return `${prettyNumber(acres, 0)} Acres`;
    }
    default:
      return null;
  }
}

function FeatureAtLocationCard({ feature, hideCoords, dispatch }) {
  const [isExpanded, setIsExpanded] = useState(true);

  const stat = getFeatureStat(feature, hideCoords);
  const createdAt = feature.properties.createdAt
    ? dayjs.unix(feature.properties.createdAt).format('MM/DD/YYYY @ HH:mm')
    : null;
  const updatedAt = feature.properties.updatedAt
    ? dayjs.unix(feature.properties.updatedAt).format('MM/DD/YYYY @ HH:mm')
    : null;

  const handleZoom = () => {
    dispatch({
      type: 'map/goToFeature',
      payload: { featureId: feature.uuid, layerId: MAP_LAYER_IDS.content },
    });
  };

  const handleViewDetails = () => {
    dispatch({ type: 'features/setSelectedFeatureLoading', payload: true });
    dispatch({ type: 'features/setIsEditingFeature', payload: false });
    dispatch({ type: 'app/gotoSidebarWithHistory', payload: SIDEBAR_VIEWS.FeatureDetail });
    dispatch({
      type: 'features/getFeature',
      payload: feature.uuid,
      callback: (feat, error) => {
        if (feat) {
          dispatch({ type: 'features/setSelectedFeature', payload: feat });
          dispatch({ type: 'features/setPreviousFeatureState', payload: feat });
          dispatch({ type: 'map/goToFeature', payload: { featureId: feat.uuid, layerId: MAP_LAYER_IDS.content } });
        } else {
          dispatch({ type: 'app/navSidebarBack' });
          dispatch({ type: 'features/setSelectedFeature', payload: null });
        }
        dispatch({ type: 'features/setSelectedFeatureLoading', payload: false });
      },
    });
  };

  return (
    <div className={styles.huntUnitsOuter}>
      <div className={styles.huntUnitsHeader} onClick={() => setIsExpanded(v => !v)}>
        <span className={featureStyles.featureIcon}>{getFeatureIcon(feature.properties.ghType)}</span>
        <span className={styles.huntUnitsTitle}>{feature.title}</span>
        <span className={`${styles.huntUnitsChevron} ${isExpanded ? styles.huntUnitsChevronOpen : ''}`}>
          <ChevronDown width={18} color='#B5B5B8' />
        </span>
      </div>

      {isExpanded && (
        <div className={styles.huntUnitsBody}>
          <div className={featureStyles.featureCard}>
            {stat && <div className={featureStyles.featureStat}>{stat}</div>}
            {updatedAt && <div className={featureStyles.featureMeta}>Last Updated {updatedAt}</div>}
            {createdAt && <div className={featureStyles.featureMeta}>Created {createdAt}</div>}

            <button className={featureStyles.featureActionButton} onClick={handleZoom}>
              <Center color='#FAFAFA' width={14} height={14} />
              Zoom to area
            </button>
            <button className={featureStyles.featureActionButtonViewDetails} onClick={handleViewDetails}>
              <Info color='#FAFAFA' width={14} height={14} />
              View details
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

FeatureAtLocationCard.propTypes = {
  feature: PropTypes.object.isRequired,
  hideCoords: PropTypes.bool,
  dispatch: PropTypes.func.isRequired,
};

export default FeatureAtLocationCard;
